/**
 * Shared React datatable primitives (search, sort headers, scroll, empty state).
 * Replaces Preline-styled table chrome with semantic tokens.
 */

import React from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export { useClientDatatable } from '../../hooks/use-client-datatable'
export { default as DatatablePagination } from './DatatablePagination'

export const DATATABLE_TABLE_CLASS = 'min-w-full'
export const DATATABLE_THEAD_CLASS = 'border-y border-border bg-card'
export const DATATABLE_TBODY_CLASS = 'divide-y divide-border'
export const DATATABLE_CELL_CLASS = 'p-3 whitespace-nowrap text-base text-muted-foreground'
export const DATATABLE_CELL_PRIMARY_CLASS = 'p-3 whitespace-nowrap text-base font-medium text-foreground'

function SortIcon({ direction }) {
  const activeAsc = direction === 'asc' ? 'text-primary' : 'text-muted-foreground/60'
  const activeDesc = direction === 'desc' ? 'text-primary' : 'text-muted-foreground/60'

  return (
    <svg
      className="ms-1 -me-0.5 size-3.5 shrink-0 text-muted-foreground"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path className={activeDesc} d="m7 15 5 5 5-5" />
      <path className={activeAsc} d="m7 9 5-5 5 5" />
    </svg>
  )
}

export function DatatableCard({ children, className = '', search, toolbar, footer }) {
  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm',
        className
      )}
    >
      {search}
      {toolbar}
      {children}
      {footer}
    </div>
  )
}

export function DatatableSearch({
  value,
  onChange,
  placeholder = 'Search…',
  id = 'datatable-search',
  className = '',
}) {
  return (
    <div className={cn('border-b border-border px-4 py-3', className)}>
      <div className="relative w-full max-w-xs">
        <label htmlFor={id} className="sr-only">
          Search
        </label>
        <Input
          type="search"
          id={id}
          value={value}
          onChange={onChange}
          className="ps-9"
          placeholder={placeholder}
        />
        <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
          <svg
            className="size-4 shrink-0 text-muted-foreground"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </div>
      </div>
    </div>
  )
}

export function DatatableScroll({ children, className = '', maxHeight, style = {} }) {
  const mergedStyle = maxHeight
    ? { ...style, maxHeight, overflowY: 'auto' }
    : { maxHeight: '70vh', overflowY: 'auto', ...style }

  return (
    <div
      className={cn(
        'overflow-x-auto',
        '[&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:bg-card',
        '[&::-webkit-scrollbar]:h-2',
        '[&::-webkit-scrollbar-thumb]:rounded-full',
        '[&::-webkit-scrollbar-thumb]:bg-muted-foreground/30',
        '[&::-webkit-scrollbar-track]:bg-muted',
        className
      )}
      style={mergedStyle}
    >
      {children}
    </div>
  )
}

export function DatatableEmpty({ colSpan, message = 'No results found' }) {
  return (
    <tr>
      <td colSpan={colSpan} className="p-0">
        <div className="flex flex-col items-center justify-center px-5 py-10 text-center">
          <svg
            className="size-6 shrink-0 text-muted-foreground"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">{message}</p>
        </div>
      </td>
    </tr>
  )
}

export function DatatableTh({
  children,
  className = '',
  sortable = true,
  excludeFromOrdering = false,
  align = 'left',
  sortKey = null,
  sort = null,
  onSort = null,
}) {
  const thAlign =
    align === 'right' ? 'text-end' : align === 'center' ? 'text-center' : 'text-start'

  const activeDir = sort && sortKey && sort.key === sortKey ? sort.dir : null

  if (!sortable || excludeFromOrdering) {
    return (
      <th
        scope="col"
        className={cn('px-3 py-2 text-sm font-normal text-muted-foreground', thAlign, className)}
      >
        {children}
      </th>
    )
  }

  return (
    <th scope="col" className={cn('group py-1 font-normal focus:outline-none', thAlign, className)}>
      <button
        type="button"
        onClick={() => sortKey && onSort && onSort(sortKey)}
        className={cn(
          'inline-flex items-center rounded-md border border-transparent px-2.5 py-1 text-sm text-muted-foreground hover:border-border hover:text-foreground focus:outline-none',
          align === 'right' && 'justify-end'
        )}
      >
        {children}
        <SortIcon direction={activeDir} />
      </button>
    </th>
  )
}

/** @deprecated Prefer named exports from Datatable.jsx */
export default function Datatable({ children, className = '' }) {
  return <div className={cn('flex flex-col', className)}>{children}</div>
}
